import React, { useState } from 'react';
import AuthLayout from '@components/Layout/auth/AuthLayout';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HOSPITAL_SRC, INDIVIDUAL_SRC, NURSE_SRC } from '@constants/images';
import { HOSPITAL, INDIVIDUAL, NURSE } from '@constants/constants';
import { LOGIN_PATH, ONBOARDING_PATH, SIGNUP_PATH } from '@constants/routes';
import {
    setDeviceUniqueID,
    setHospitalName,
    setHospitalBranch,
    setHospitalID,
    setAddressOne,
    setAddressTwo,
    setCity,
    setUserState,
    setCountry,
    setPincode,
    setHospitalWebsite,
    setHospitalEmail,
    setHospitalContact,
    setHospitalGST,
    setFileMetadata,
    setName,
    setEmail,
    setMobileNumber,
    setPassword,
    setUserRole,
} from '@store/reducers/auth/signupSlice';

interface PremiumUserRoleOptionProps {
    choice: string;
    selectedChoice: string;
    selectChoice: (choice: string) => void;
    imageSrc: string;
    label: string;
    description: string;
}

const UserRoleOption: React.FC<PremiumUserRoleOptionProps> = ({
    choice,
    selectedChoice,
    selectChoice,
    imageSrc,
    label,
    description,
}) => {
    const { t } = useTranslation();
    const isSelected = selectedChoice === choice;
    return (
        <div
            className={`relative flex flex-col justify-between w-[220px] min-h-[285px] p-4 bg-white rounded-2xl cursor-pointer transition-all duration-300 select-none border-2 ${
                isSelected
                    ? 'border-primary shadow-lg transform -translate-y-1'
                    : 'user-role-option-idle border-gray-100 hover:shadow-md hover:border-gray-200'
            }`}
            onClick={() => selectChoice(choice)}
        >
            {/* Top-Right Badge */}
            {isSelected && (
                <div className="absolute top-4 right-4 bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center shadow-sm">
                    <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="3"
                            d="M5 13l4 4L19 7"
                        ></path>
                    </svg>
                </div>
            )}

            {/* Image Illustration */}
            <div className="flex justify-center items-center h-[90px] mt-1">
                <img
                    src={imageSrc}
                    alt={t(label)}
                    className="object-contain h-full max-h-[86px]"
                />
            </div>

            {/* Title & Description */}
            <div className="text-center mt-3 flex-grow flex flex-col justify-start">
                <h5 className="text-primary text-base font-bold mb-2">
                    {t(label)}
                </h5>
                <p className="text-gray-500 text-xs leading-relaxed font-medium px-1">
                    {description}
                </p>
            </div>
        </div>
    );
};

const UserRole = () => {
    const [selectedChoice, setSelectedChoice] = useState<string>(INDIVIDUAL);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const fromOnboarding = useAppSelector(
        (state) => state.signup.fromOnboarding
    );

    function selectChoice(choice: string) {
        setSelectedChoice(choice);
    }

    function nextPage() {
        dispatch(setUserRole(selectedChoice));
        dispatch(setDeviceUniqueID(''));
        dispatch(setHospitalName(''));
        dispatch(setHospitalBranch(''));
        dispatch(setHospitalID(''));
        dispatch(setAddressOne(''));
        dispatch(setAddressTwo(''));
        dispatch(setCity(''));
        dispatch(setUserState(''));
        dispatch(setCountry(''));
        dispatch(setPincode(''));
        dispatch(setHospitalWebsite(''));
        dispatch(setHospitalEmail(''));
        dispatch(setHospitalContact(''));
        dispatch(setHospitalGST(''));
        dispatch(
            setFileMetadata({
                name: '',
                url: '',
                type: '',
            })
        );
        dispatch(setName(''));
        dispatch(setEmail(''));
        dispatch(setMobileNumber(''));
        dispatch(setPassword(''));
        navigate(SIGNUP_PATH);
    }

    const handleBackClick = () => {
        if (fromOnboarding) {
            navigate(ONBOARDING_PATH);
        } else {
            navigate(LOGIN_PATH);
        }
    };

    return (
        <AuthLayout
            onBackClick={() => handleBackClick()}
            className="relative min-h-screen bg-cover bg-center overflow-x-hidden flex flex-col justify-between"
            style={{
                background:
                    "linear-gradient(90deg, rgba(243, 246, 255, 0.98) 0%, rgba(243, 246, 255, 0.85) 45%, rgba(255, 255, 255, 0.15) 100%), url('/public/images/hospital_bg.png') no-repeat right center / cover",
            }}
        >
            {/* Background elements */}
            {/* Floating plus signs */}
            <div className="absolute top-[28%] left-[20%] text-blue-200 text-3xl font-extralight select-none pointer-events-none opacity-40 z-0">
                +
            </div>
            <div className="absolute bottom-[28%] left-[16%] text-blue-200 text-2xl font-extralight select-none pointer-events-none opacity-30 z-0">
                +
            </div>
            <div className="absolute top-[55%] left-[24%] text-blue-100 text-4xl font-extralight select-none pointer-events-none opacity-50 z-0">
                +
            </div>

            {/* Grid dot pattern on the top-right */}
            <svg
                className="absolute top-8 right-8 w-32 h-32 pointer-events-none opacity-[0.25] z-0"
                fill="#3b82f6"
            >
                <defs>
                    <pattern
                        id="dotGrid"
                        x="0"
                        y="0"
                        width="16"
                        height="16"
                        patternUnits="userSpaceOnUse"
                    >
                        <circle cx="2" cy="2" r="1.2" />
                    </pattern>
                </defs>
                <rect width="128" height="128" fill="url(#dotGrid)" />
            </svg>

            {/* Bottom-left line waves */}
            <svg
                className="absolute bottom-0 left-0 w-[450px] h-[250px] pointer-events-none opacity-[0.35] z-0"
                viewBox="0 0 450 250"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M-20 250 C 80 230, 120 180, 180 160 C 240 140, 300 170, 450 120"
                    stroke="#bfdbfe"
                    strokeWidth="1"
                />
                <path
                    d="M-20 250 C 60 210, 100 160, 160 140 C 220 120, 280 150, 450 100"
                    stroke="#93c5fd"
                    strokeWidth="1"
                    opacity="0.6"
                />
                <path
                    d="M-20 250 C 100 240, 140 190, 200 180 C 260 170, 320 200, 450 140"
                    stroke="#dbeafe"
                    strokeWidth="1.2"
                    opacity="0.8"
                />
                <path
                    d="M-20 250 C 40 190, 80 140, 140 120 C 200 100, 260 130, 450 80"
                    stroke="#eff6ff"
                    strokeWidth="0.8"
                    opacity="0.5"
                />
            </svg>

            {/* Bottom-right line waves */}
            <svg
                className="absolute bottom-0 right-0 w-[450px] h-[250px] pointer-events-none opacity-[0.35] z-0"
                viewBox="0 0 450 250"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M470 250 C 370 230, 330 180, 270 160 C 210 140, 150 170, 0 120"
                    stroke="#bfdbfe"
                    strokeWidth="1"
                />
                <path
                    d="M470 250 C 390 210, 350 160, 290 140 C 230 120, 170 150, 0 100"
                    stroke="#93c5fd"
                    strokeWidth="1"
                    opacity="0.6"
                />
                <path
                    d="M470 250 C 350 240, 310 190, 250 180 C 190 170, 130 200, 0 140"
                    stroke="#dbeafe"
                    strokeWidth="1.2"
                    opacity="0.8"
                />
                <path
                    d="M470 250 C 410 190, 370 140, 310 120 C 250 100, 190 130, 0 80"
                    stroke="#eff6ff"
                    strokeWidth="0.8"
                    opacity="0.5"
                />
            </svg>

            {/* Sparkles / Glitter background behind the device */}
            <svg
                className="absolute left-[1%] top-[50%] -translate-y-[50%] w-[320px] h-[580px] pointer-events-none z-0"
                viewBox="0 0 320 580"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <defs>
                    <radialGradient id="glitterGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                        <stop offset="30%" stopColor="#93c5fd" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                    </radialGradient>
                    <radialGradient id="deviceAura" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.28" />
                        <stop offset="55%" stopColor="#60a5fa" stopOpacity="0.12" />
                        <stop offset="100%" stopColor="#dbeafe" stopOpacity="0" />
                    </radialGradient>
                </defs>

                {/* Soft blue glow aura behind the device */}
                <ellipse cx="160" cy="290" rx="120" ry="220" fill="url(#deviceAura)" />

                {/* Sparkle 1 */}
                <path d="M 60 140 Q 60 150 70 150 Q 60 150 60 160 Q 60 150 50 150 Q 60 150 60 140 Z" fill="#ffffff" opacity="0.9" />
                <circle cx="60" cy="150" r="12" fill="url(#glitterGlow)" opacity="0.6" />

                {/* Sparkle 2 */}
                <path d="M 240 220 Q 240 226 246 226 Q 240 226 240 232 Q 240 226 234 226 Q 240 226 240 220 Z" fill="#ffffff" opacity="0.95" />
                <circle cx="240" cy="226" r="8" fill="url(#glitterGlow)" opacity="0.7" />

                {/* Sparkle 3 */}
                <path d="M 110 380 Q 110 388 118 388 Q 110 388 110 396 Q 110 388 102 388 Q 110 388 110 380 Z" fill="#ffffff" opacity="0.85" />
                <circle cx="110" cy="388" r="10" fill="url(#glitterGlow)" opacity="0.5" />

                {/* Sparkle 4 */}
                <path d="M 280 430 Q 280 435 285 435 Q 280 435 280 440 Q 280 435 275 435 Q 280 435 280 430 Z" fill="#ffffff" opacity="0.9" />
                <circle cx="280" cy="435" r="6" fill="url(#glitterGlow)" opacity="0.6" />

                {/* Sparkle 5 */}
                <path d="M 180 80 Q 180 85 185 85 Q 180 85 180 90 Q 180 85 175 85 Q 180 85 180 80 Z" fill="#ffffff" opacity="0.8" />
                <circle cx="180" cy="85" r="6" fill="url(#glitterGlow)" opacity="0.5" />

                {/* Sparkle 6 */}
                <path d="M 70 490 Q 70 498 78 498 Q 70 498 70 506 Q 70 498 62 498 Q 70 498 70 490 Z" fill="#ffffff" opacity="0.9" />
                <circle cx="70" cy="498" r="10" fill="url(#glitterGlow)" opacity="0.6" />

                {/* Tiny glitter dots */}
                <circle cx="45" cy="200" r="2" fill="#ffffff" opacity="0.8" />
                <circle cx="130" cy="180" r="1.5" fill="#93c5fd" opacity="0.9" />
                <circle cx="80" cy="280" r="2.5" fill="#ffffff" opacity="0.75" />
                <circle cx="210" cy="320" r="2" fill="#93c5fd" opacity="0.85" />
                <circle cx="250" cy="110" r="1.5" fill="#ffffff" opacity="0.9" />
                <circle cx="160" cy="450" r="3" fill="#ffffff" opacity="0.7" />
                <circle cx="160" cy="450" r="8" fill="url(#glitterGlow)" opacity="0.4" />
                <circle cx="200" cy="410" r="2" fill="#93c5fd" opacity="0.8" />
                <circle cx="90" cy="420" r="1.5" fill="#ffffff" opacity="0.95" />
                <circle cx="240" cy="500" r="2" fill="#ffffff" opacity="0.8" />
                <circle cx="140" cy="520" r="2" fill="#93c5fd" opacity="0.9" />
            </svg>

            {/* Semi-transparent DripSet device on the left */}
            <div
                className="absolute left-[3%] top-[50%] -translate-y-[50%] w-[260px] h-[520px] bg-contain bg-no-repeat bg-center pointer-events-none opacity-[0.50] z-0"
                style={{ backgroundImage: "url('/public/images/dripset.png')" }}
            />

            {/* Centered Main Area */}
            <div className="flex-grow flex flex-col items-center justify-center py-3 px-4 z-10">
                <div className="text-center mb-3">
                    <h4 className="text-2xl font-extrabold text-gray-900 mb-1">
                        {t('auth.user_role.title')}
                    </h4>
                    <p className="text-sm text-gray-600 font-semibold max-w-md mx-auto leading-relaxed mb-0">
                        {t('auth.user_role.discription.text')}
                        <br />
                        {t('auth.user_role.discription.text_continued')}
                    </p>
                </div>

                {/* Cards Container */}
                <div className="flex flex-row justify-center items-center gap-4 mt-2 mb-3 flex-wrap">
                    <UserRoleOption
                        choice={INDIVIDUAL}
                        selectedChoice={selectedChoice}
                        selectChoice={selectChoice}
                        imageSrc={INDIVIDUAL_SRC}
                        label="auth.user_role.individual"
                        description={t('auth.user_role.individual_desc', {
                            defaultValue:
                                "Register as an individual user to manage your or your loved one's IV therapy.",
                        })}
                    />
                    <UserRoleOption
                        choice={NURSE}
                        selectedChoice={selectedChoice}
                        selectChoice={selectChoice}
                        imageSrc={NURSE_SRC}
                        label="auth.user_role.nurse"
                        description={t('auth.user_role.nurse_desc', {
                            defaultValue:
                                'Register as a nurse to monitor and manage patient IV therapy efficiently.',
                        })}
                    />
                    <UserRoleOption
                        choice={HOSPITAL}
                        selectedChoice={selectedChoice}
                        selectChoice={selectChoice}
                        imageSrc={HOSPITAL_SRC}
                        label="auth.user_role.hospital"
                        description={t('auth.user_role.hospital_desc', {
                            defaultValue:
                                'Register as a hospital professional to oversee and manage IV therapy across your facility.',
                        })}
                    />
                </div>

                {/* Next Button */}
                <button
                    className="flex items-center justify-center gap-2 mt-2 text-white !bg-primary hover:bg-blue-800 transition-all duration-300 font-bold rounded-lg text-base px-8 py-2.5 shadow-md w-56 z-10"
                    onClick={nextPage}
                >
                    <span>{t('auth.user_role.next')}</span>
                    <svg
                        className="w-5 h-5 ml-1 inline-block"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                        ></path>
                    </svg>
                </button>

                {/* Encryption Badge */}
                <div className="flex items-center justify-center gap-1.5 mt-3 text-xs font-semibold text-gray-500 z-10 select-none">
                    <svg
                        className="w-5 h-5 text-primary flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l5-5z"
                            clipRule="evenodd"
                        />
                    </svg>
                    <span>Your information is secure and encrypted</span>
                </div>
            </div>
        </AuthLayout>
    );
};

export default UserRole;
